import '../../../sass/alpha-modules/optional/_header-states.scss'

import AlphaFilters from '../../abstracts/classes/alpha-filters'
import AlphaHelpers from '../../abstracts/classes/alpha-helpers'
import AlphaModule from '../../abstracts/classes/alpha-module'
import debounce from 'lodash-es/debounce'

export default class HeaderStates extends AlphaModule {
  constructor(...args) {
    super(...args)

    this._headerEl = document.querySelector('header.site-header')
  }

  getName() {
    return 'headerStates'
  }

  getDefaultConfig() {
    return {
      states: {
        // eslint-disable-next-line camelcase
        hidden_on_scroll_down: false,
        // eslint-disable-next-line camelcase
        shrunk_logo: false,
        // eslint-disable-next-line camelcase
        swapped_logo: false,
        transparent: false
      },
      stateClass: 'is-%state%'
    }
  }

  _onLoad() {
    super._onLoad()

    this._handlePageScroll()
  }

  _handlePageScroll() {
    const moduleName = this.getName()
    const headerEl = this.getHeaderElement()
    if (headerEl) {
      // initial changes based on page scroll module
      const manager = this._getManager()
      const pageScrollModule = manager.getModule('pageScroll')
      if (pageScrollModule) {
        this._handleStateChangesOnScroll(pageScrollModule.pageIsScrolled())
      }

      // listen to page scroll changes and update states accordingly
      manager.addModuleEventListener('pageScroll', ['reset', 'scroll'], (detail) => {
        const pageScrolled = detail.eventName === 'scroll'
        this._handleStateChangesOnScroll(pageScrolled)
      })

      // listen to scroll direction changes and update states accordingly
      if (this.hasState('hidden_on_scroll_down')) {
        this._handleStateChangesOnScrollDirection()
      }

      // listen to hover changes and update class accordingly
      const isOffcanvas = document.querySelector('.site-header[class*="nav-offcanvas"]') !== null
      if (AlphaFilters.applyFilters(`module.${moduleName}.useHoverClass`, !isOffcanvas)) {
        this._handleStateChangesOnHover()
      }
    }
  }

  _handleStateChangesOnScroll(pageScrolled = false) {
    const states = this.getConfig().states
    for (const state in states) {
      if (AlphaHelpers.objectHasOwnProperty(states, state)) {
        const enabled = states[state]
        if (enabled) {
          let set = false
          switch (state) {
            case 'transparent':
              set = !pageScrolled
              break
            case 'hidden_on_scroll_down':
            case 'shrunk_logo':
            case 'swapped_logo':
              set = pageScrolled
              break
            default:
              break
          }

          this.changeState(state, set)
        }
      }
    }
  }

  _handleStateChangesOnScrollDirection() {
    let scrollTop = window.pageYOffset || document.documentElement.scrollTop
    let scrollDirection = ''

    const debounceDelay = 50
    const onScroll = debounce(() => {
      const prevScrollDirection = scrollDirection
      const st = window.pageYOffset || document.documentElement.scrollTop
      if (st > scrollTop && scrollDirection !== 'down') {
        scrollDirection = 'down'
      } else if (st < scrollTop && scrollDirection !== 'up') {
        scrollDirection = 'up'
      }

      scrollTop = st <= 0 ? 0 : st

      if (scrollDirection !== prevScrollDirection) {
        this.changeScrollDirection(scrollDirection, prevScrollDirection)
      }
    }, debounceDelay)

    window.addEventListener('scroll', onScroll, false)
  }

  _handleStateChangesOnHover() {
    const headerEl = this.getHeaderElement()
    if (headerEl) {
      headerEl.addEventListener('mouseenter', () => {
        if (AlphaHelpers.isDesktop()) {
          headerEl.classList.add('is-hovered')
        }
      })

      headerEl.addEventListener('mouseleave', () => {
        if (AlphaHelpers.isDesktop()) {
          headerEl.classList.remove('is-hovered')
        }
      })
    }
  }

  changeState(state, set = false) {
    const headerEl = this.getHeaderElement()
    if (headerEl) {
      const stateClass = this.getStateClass(state)
      if (set) {
        headerEl.classList.add(stateClass)
      } else {
        headerEl.classList.remove(stateClass)
      }
    }

    // inform other components that this state changed
    this.dispatchEvent('state.changed', {
      state,
      set
    }, headerEl)
  }

  changeScrollDirection(direction, oldDirection) {
    const headerEl = this.getHeaderElement()
    if (headerEl) {
      if (oldDirection.length) {
        headerEl.classList.remove(`scroll-${oldDirection}`)
      }

      headerEl.classList.add(`scroll-${direction}`)
    }
  }

  getHeaderElement() {
    return this._headerEl
  }

  hasState(state) {
    const states = this.getConfig().states

    return states[state] || false
  }

  isStateActive(state) {
    const headerEl = this.getHeaderElement()
    if (headerEl) {
      return headerEl.classList.contains(this.getStateClass(state))
    }

    return false
  }

  getStateClass(state) {
    const config = this.getConfig()

    return config.stateClass.replace('%state%', state)
  }
}
